import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import { ListPlus, Loader2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddToListDialog = ({ objectType, ids, open, onOpenChange, onDone }) => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lists`, { params: { object_type: objectType } });
      setLists((res.data.lists || []).filter(l => l.list_type === 'static'));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [objectType]);

  useEffect(() => { if (open) fetchLists(); }, [open, fetchLists]);

  const addTo = async (listId) => {
    setAdding(listId);
    try {
      await axios.post(`${API}/lists/${listId}/members`, { ids, action: 'add' });
      toast.success(`Added ${ids.length} to list`);
      onOpenChange(false);
      if (onDone) onDone();
    } catch { toast.error('Failed to add to list'); }
    finally { setAdding(null); }
  };

  const createAndAdd = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/lists`, {
        name: newName.trim(), object_type: objectType, list_type: 'static', member_ids: ids,
      });
      toast.success(`List "${res.data.name}" created with ${ids.length} members`);
      setNewName('');
      onOpenChange(false);
      if (onDone) onDone();
    } catch { toast.error('Failed to create list'); }
    finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="add-to-list-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2">
            <ListPlus className="w-5 h-5 text-violet-400" /> Add to list
          </DialogTitle>
          <DialogDescription className="text-slate-400">Add {ids.length} selected {objectType}{ids.length === 1 ? '' : 's'} to a static list.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2 max-h-[40vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>
          ) : lists.length === 0 ? (
            <p className="text-xs text-slate-600 italic">No static lists yet. Create one below.</p>
          ) : lists.map(l => (
            <button
              key={l.id}
              onClick={() => addTo(l.id)}
              disabled={adding === l.id}
              className="w-full flex items-center justify-between bg-slate-950 border border-slate-800 hover:border-violet-700 rounded-md px-3 py-2 text-left transition-colors"
              data-testid={`add-to-list-${l.id}`}
            >
              <span className="text-sm text-white">{l.name}</span>
              <span className="text-xs text-slate-500">{adding === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : `${l.member_count} members`}</span>
            </button>
          ))}
        </div>

        <form onSubmit={createAndAdd} className="flex gap-2 border-t border-slate-800 pt-3">
          <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New list name" className="bg-slate-950 border-slate-800 h-9" data-testid="new-list-name" />
          <Button type="submit" size="sm" disabled={creating || !newName.trim()} className="bg-violet-600 hover:bg-violet-500" data-testid="create-list-add-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
          </Button>
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToListDialog;
